import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { User } from "@propelauth/javascript";

import {
  EnvDataType,
  IntegrationOverrides,
  OutputFormat,
} from "src/ts-types/ApiTypes";
import { api } from "services/appSlice";
import { mapAPI } from "services/mapSlice";
import { MeApiResponse } from "@repo/ping-react-js";
import { isPingDataPlan, PingDataPlan } from "src/constants/plans";

type PingSettingsInitialState = {
  ENV: EnvDataType;
  resolvedSovid: string | null;
  resolvedPingid: string | null;
  // The raw URL id (pingid or sovid) that resolvedSovid was resolved from.
  // useSovid() only trusts resolvedSovid when this still matches the current URL,
  // which keeps stale values from leaking across navigation while letting refetches
  // of the same id keep the existing resolution.
  resolvedFor: string | null;
  ssoUser: User | null;
  permissions: string[];
  pingDataPlan: PingDataPlan | null;
  isStaff: boolean;
  isPingEmployee: boolean;
  useHeatMap: boolean;
  copeSectionsAmt: number;
  outputFormats: OutputFormat[];
  integrationOverrides: IntegrationOverrides;
  autopullIntegrations: string[] | null;
  integrationSettings: Record<string, string>;
  enableBetaFeatures: boolean;
  isCatModelingReadonly: boolean;
};

const settingsSlice = createSlice({
  name: "settings",
  initialState: {
    ssoUser: null,
    permissions: [],
    pingDataPlan: null,
    isStaff: false,
    isPingEmployee: false,
    ENV: {},
    resolvedSovid: null,
    resolvedPingid: null,
    resolvedFor: null,
    copeSectionsAmt: 2,
    outputFormats: [],
    integrationOverrides: {},
    autopullIntegrations: null,
    integrationSettings: {},
    enableBetaFeatures: ["local", "dev"].includes(import.meta.env.VITE_APP_ENV),
    isCatModelingReadonly: false,
  } as PingSettingsInitialState,
  reducers: {
    setUser(state, actions: PayloadAction<User | null>) {
      state.ssoUser = actions.payload;
    },
    setEnvironment(state, actions: PayloadAction<EnvDataType>) {
      state.ENV = actions.payload;
    },
    setUseHeatMap(state) {
      state.useHeatMap = !state.useHeatMap;
    },
    setEnableBetaFeatures(state, action: PayloadAction<boolean>) {
      state.enableBetaFeatures = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(api.endpoints.getEnv.matchFulfilled, (state, action) => {
      state.ENV = action.payload;
      // Only pingid URLs need a pingid → sovid resolution. Or else useSovid() falls back to null.
      const requestedSovid = action.meta.arg.originalArgs.sovid;
      if (action.payload.resolved_pingid) {
        state.resolvedPingid = action.payload.resolved_pingid;
      }

      if (requestedSovid?.startsWith("p-")) {
        state.resolvedSovid = action.payload.resolved_sovid ?? null;
        state.resolvedFor = requestedSovid;
      }
    });
    builder.addMatcher(
      api.endpoints.getMe.matchFulfilled,
      (state, action: PayloadAction<MeApiResponse>) => {
        state.permissions = action.payload.permissions;
        state.pingDataPlan = isPingDataPlan(action.payload.ping_data_plan)
          ? action.payload.ping_data_plan
          : null;
        state.isStaff = action.payload.is_staff;
        state.isPingEmployee = action.payload.is_ping_employee;
      },
    );
    builder.addMatcher(
      mapAPI.endpoints.getLayerSettings.matchFulfilled,
      (state, action) => {
        state.outputFormats = action.payload.pingmaps.output_formats || [];
        state.integrationOverrides =
          action.payload.pingmaps.integration_overrides || {};
        state.autopullIntegrations =
          action.payload.pingmaps.auto_pull_integrations || null;
        state.integrationSettings = action.payload.pingmaps.integrations || {};
      },
    );
    builder.addMatcher(
      mapAPI.endpoints.getPolicyOverview.matchFulfilled,
      (state, action: any) => {
        const constFields = action.payload?.field_overrides?.construction;
        const occupancyFields = action.payload?.field_overrides?.occupancy;

        const constAmt = constFields?.length || 1;
        const occupancyAmt = occupancyFields?.length || 1;
        state.copeSectionsAmt = constAmt + occupancyAmt;
      },
    );
  },
});

const { actions, reducer } = settingsSlice;

export const { setUser, setEnableBetaFeatures } = actions;

export default reducer;
